// Nástroj „Řazení v kategoriích": e-shop (CZ / SK) → kategorie → přetažením
// seřadit produkty → Uložit (Shoptet „Priorita" v kategorii, vyšší = výš).
// SK se řadí zvlášť (rozhodnutí uživatele — kategorie CZ/SK se nepárují).

import { Toast } from '@/components/Toast';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CategoryBreadcrumb } from './CategoryBreadcrumb';
import { CategoryOrderGrid, type OrderItem } from './CategoryOrderGrid';

type Category = {
  guid: string;
  name: string;
  path: string;
  url: string | null;
  parentGuid: string | null;
  visible: boolean;
  productOrdering: string | null;
};

// Řazení, u kterých se priorita na e-shopu projeví (ostatní řadí podle ceny, data…).
const PRIORITY_ORDERINGS = ['default', 'category-priority', 'category-priority-desc'];

const ESHOPS = [
  { code: 'v-mart-cz', label: 'CZ' },
  { code: 'v-mart-sk', label: 'SK' },
] as const;

export function RazeniKategoriiPage() {
  const [eshop, setEshop] = useState<string>('v-mart-cz');
  const [categories, setCategories] = useState<Category[] | null>(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Category | null>(null);
  const [items, setItems] = useState<OrderItem[] | null>(null);
  const [original, setOriginal] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setCategories(null);
    setSelected(null);
    setItems(null);
    apiFetch<{ categories: Category[] }>(`/api/razeni-kategorii/kategorie?eshop=${eshop}`)
      .then((r) => setCategories(r.categories))
      .catch((e: unknown) =>
        setToast({
          msg: e instanceof ApiError ? e.describe() : 'Kategorie se nenačetly',
          kind: 'error',
        }),
      );
  }, [eshop]);

  const loadProducts = useCallback(
    async (cat: Category) => {
      setSelected(cat);
      setItems(null);
      setLoading(true);
      try {
        const r = await apiFetch<{ items: OrderItem[] }>(
          `/api/razeni-kategorii/produkty?eshop=${eshop}&guid=${cat.guid}`,
        );
        setItems(r.items);
        setOriginal(r.items.map((i) => i.guid).join(','));
      } catch (e: unknown) {
        setToast({
          msg: e instanceof ApiError ? e.describe() : 'Produkty se nenačetly',
          kind: 'error',
        });
      } finally {
        setLoading(false);
      }
    },
    [eshop],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!categories) return [];
    const list = q ? categories.filter((c) => c.path.toLowerCase().includes(q)) : categories;
    return list.slice(0, 50);
  }, [categories, query]);

  const dirty = !!items && items.map((i) => i.guid).join(',') !== original;

  /** Přepnout kategorii (vyhledávání, drobečková navigace, podkategorie). */
  function selectCategory(c: Category) {
    if (dirty && !confirm('Neuložené změny pořadí se zahodí. Pokračovat?')) return;
    loadProducts(c);
  }

  async function save() {
    if (!items || !selected) return;
    setSaving(true);
    try {
      const r = await apiFetch<{ saved: number }>('/api/razeni-kategorii/ulozit', {
        method: 'POST',
        body: { eshop, guid: selected.guid, order: items.map((i) => i.guid) },
      });
      setOriginal(items.map((i) => i.guid).join(','));
      setToast({ msg: `Uloženo — pořadí ${r.saved} produktů`, kind: 'success' });
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.describe() : 'Uložení selhalo', kind: 'error' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text">Řazení v kategoriích</h1>
          <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
            Vyber kategorii a přetažením seřaď produkty. Uložení nastaví v Shoptetu prioritu
            produktů v kategorii (Produkt týdne 🏆 zůstává nahoře).
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-border bg-bg p-1">
          {ESHOPS.map((e) => (
            <button
              key={e.code}
              type="button"
              onClick={() => setEshop(e.code)}
              className={`px-4 py-1.5 text-sm font-semibold rounded-md cursor-pointer ${
                eshop === e.code ? 'bg-surface text-text shadow-sm' : 'text-text-secondary'
              }`}
            >
              {e.label}
            </button>
          ))}
        </div>
      </div>

      <label htmlFor="razeni-hledat" className="block text-sm font-semibold text-text mb-1.5">
        Vyber kategorii
      </label>
      <div className="relative mb-5 max-w-xl">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-primary pointer-events-none"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
        <input
          id="razeni-hledat"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={
            categories ? 'Napiš název kategorie, např. „zateplené"…' : 'Načítám kategorie…'
          }
          disabled={!categories}
          className="w-full text-base text-text bg-surface border-2 border-primary/40 rounded-xl pl-11 pr-4 py-3 shadow-sm outline-none placeholder:text-text-muted hover:border-primary/70 focus:border-primary focus:ring-4 focus:ring-primary-100 transition-all disabled:opacity-60"
        />
        {query.trim() && filtered.length > 0 && (
          <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-surface border border-border rounded-lg shadow-card-hover max-h-80 overflow-y-auto">
            {filtered.map((c) => (
              <button
                key={c.guid}
                type="button"
                onClick={() => {
                  setQuery('');
                  selectCategory(c);
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-bg cursor-pointer"
              >
                <span className="text-text">{c.path}</span>
                {!c.visible && <span className="ml-2 text-xs text-text-muted">(skrytá)</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <CategoryBreadcrumb
              categories={categories ?? []}
              selected={selected}
              onSelect={selectCategory}
            />
            {selected.url && (
              <a
                href={selected.url}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-primary underline"
              >
                Otevřít na e-shopu
              </a>
            )}
            {selected.productOrdering && !PRIORITY_ORDERINGS.includes(selected.productOrdering) && (
              <div className="text-xs text-danger mt-1">
                Pozor: kategorie má v Shoptetu řazení „{selected.productOrdering}" — priorita se na
                e-shopu projeví jen při výchozím řazení.
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Ukládám…' : dirty ? 'Uložit pořadí' : 'Uloženo'}
          </button>
        </div>
      )}

      {loading && <div className="text-sm text-text-muted">Načítám produkty…</div>}
      {items && items.length === 0 && (
        <div className="text-sm text-text-muted">V kategorii nejsou žádné produkty.</div>
      )}
      {items && items.length > 0 && (
        <div className="mb-3 text-xs text-text-muted">
          Pod produktem: velikosti skladem a prodané kusy za posledních 30 dní
          {eshop === 'v-mart-sk' ? ' (sklad i prodeje podle CZ — SK se do Tools nesynchronizují, produkty jsou stejné)' : ''}.
          Pruh je poměrný k nejprodávanějšímu produktu kategorie.
        </div>
      )}
      {items && items.length > 0 && (
        <CategoryOrderGrid
          items={items}
          onChange={setItems}
          salesNote={eshop === 'v-mart-sk' ? 'podle CZ objednávek' : undefined}
        />
      )}

      {toast && <Toast message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />}
    </>
  );
}
